import React, { useEffect, useRef, useState } from 'react';
import { Power, DollarSign, Package, Star, Bike, MapPin, CheckCircle2, XCircle, Loader2, ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { driverApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import ShopAnywhereCallout from './ShopAnywhereCallout';
import QrPickupScanner from './QrPickupScanner';
import PodPhotoUploader from './PodPhotoUploader';

const STATUS_FLOW = ['confirmed', 'picked_up', 'on_the_way', 'delivered'];
const STATUS_LABEL = { placed: 'Placed', confirmed: 'Confirmed', preparing: 'Preparing', picked_up: 'Picked up', on_the_way: 'On the way', delivered: 'Delivered' };

const DriverDashboard = () => {
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanFor, setScanFor] = useState(null); // order_id we're scanning for
  const watchRef = useRef(null);
  const { t } = useTranslation();

  const load = async () => {
    try {
      const [s, o] = await Promise.all([driverApi.stats(), driverApi.orders(true)]);
      setStats(s); setActive(o);
    } catch { /* */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const toggleOnline = async () => {
    setBusy(true);
    try {
      if (stats?.is_online) {
        await driverApi.goOffline();
        toast.success('You are offline');
        if (watchRef.current && navigator.geolocation) {
          navigator.geolocation.clearWatch(watchRef.current);
          watchRef.current = null;
        }
      } else {
        if (!navigator.geolocation) throw new Error('Geolocation not supported');
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000 }));
        await driverApi.goOnline(pos.coords.latitude, pos.coords.longitude);
        toast.success('You are online');
        watchRef.current = navigator.geolocation.watchPosition(
          (p) => driverApi.updateLocation(p.coords.latitude, p.coords.longitude).catch(() => {}),
          () => {}, { enableHighAccuracy: false, maximumAge: 30000 },
        );
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const accept = async (id) => { try { await driverApi.acceptOrder(id); toast.success('Accepted'); load(); } catch { toast.error('Failed'); } };
  const decline = async (id) => { try { await driverApi.declineOrder(id); toast.success('Declined — reassigning'); load(); } catch { toast.error('Failed'); } };
  const advance = async (id, current) => {
    const idx = STATUS_FLOW.indexOf(current);
    const next = idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : 'delivered';
    try { await driverApi.updateOrderStatus(id, next); toast.success(`→ ${STATUS_LABEL[next]}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Driver dashboard</h1>
          <p className="text-slate-500 mt-1">Toggle online to start receiving orders.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 flex items-center gap-3">
          <Power className={`h-4 w-4 ${stats?.is_online ? 'text-emerald-600' : 'text-slate-400'}`} />
          <div className="text-sm">
            <div className="font-medium text-slate-900">{stats?.is_online ? 'Online' : 'Offline'}</div>
            <div className="text-xs text-slate-500">{stats?.is_busy ? 'On a delivery' : 'Idle'}</div>
          </div>
          <Switch checked={!!stats?.is_online} onCheckedChange={toggleOnline} disabled={busy} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Deliveries today" value={stats?.deliveries_today ?? '—'} sub={`${stats?.deliveries_total ?? 0} all-time`} accent="bg-blue-50 text-blue-700" />
        <StatCard icon={DollarSign} label="Earnings today" value={fmt(stats?.earnings_today)} accent="bg-emerald-50 text-emerald-700" />
        <StatCard icon={DollarSign} label="Total earnings" value={fmt(stats?.earnings_total)} accent="bg-rose-50 text-rose-700" />
        <StatCard icon={Star} label="Rating" value={(stats?.rating ?? 5).toFixed(1)} sub={`${stats?.active_assignments ?? 0} active`} accent="bg-amber-50 text-amber-700" />
      </div>

      <section className="mt-6">
        <h2 className="font-semibold text-slate-900 mb-3">Active assignments</h2>
        {active.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            {stats?.is_online ? 'Waiting for new orders… you’ll be notified when one is matched to you.' : 'Go online to start receiving orders.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {active.map((o) => (
              <div key={o.id} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-xs text-slate-500 flex items-center gap-2">
                      {o.id}
                      <ShopAnywhereCallout order={o} compact />
                    </div>
                    <h3 className="font-semibold text-slate-900 mt-0.5">{o.vendor_name || o.vendorName}</h3>
                    <div className="text-sm text-slate-500 mt-0.5">{o.items?.length} items • {fmt(o.total)}</div>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border-0 hover:bg-emerald-50 capitalize">{STATUS_LABEL[o.status] || o.status?.replace(/_/g, ' ')}</Badge>
                </div>
                <div className="mt-3 text-sm text-slate-600 space-y-1">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{o.address}</div>
                  {o.driver_distance_km !== undefined && (
                    <div className="flex items-center gap-2"><Bike className="h-4 w-4 text-slate-400" />{o.driver_distance_km} km from store</div>
                  )}
                </div>
                <ShopAnywhereCallout order={o} />
                {o.driver_accepted && (o.status === 'on_the_way' || o.status === 'picked_up') && (
                  <div className="mt-3">
                    <PodPhotoUploader
                      orderId={o.id}
                      value={(o.pod_photos || []).map((id) => id.startsWith('http') ? id : `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`)}
                      onChange={() => load()} />
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {!o.driver_accepted ? (
                    <>
                      <Button onClick={() => accept(o.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`accept-${o.id}-btn`}><CheckCircle2 className="h-4 w-4 mr-1.5" />Accept</Button>
                      <Button onClick={() => decline(o.id)} variant="outline" className="text-rose-600 hover:text-rose-700 border-rose-200"><XCircle className="h-4 w-4 mr-1.5" />Decline</Button>
                    </>
                  ) : o.status === 'confirmed' || o.status === 'preparing' ? (
                    <Button onClick={() => setScanFor(o.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`scan-${o.id}-btn`}>
                      <ScanLine className="h-4 w-4 mr-1.5" />{t('driver.scan_to_pickup', 'Scan to pick up')}
                    </Button>
                  ) : (
                    <Button onClick={() => advance(o.id, o.status)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`advance-${o.id}-btn`}>
                      {o.status === 'on_the_way' ? t('driver.mark_delivered', 'Mark delivered') : o.status === 'picked_up' ? 'Start delivery' : 'Mark picked up'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <QrPickupScanner
        orderId={scanFor}
        open={scanFor != null}
        onClose={() => setScanFor(null)}
        onSuccess={() => load()} />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`h-10 w-10 rounded-xl ${accent} flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default DriverDashboard;
